import { DotsThree, Info, PlusIcon, UserPlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreatedDistrictManagers } from "@/api/reseller/queries";
import type { CreatedDistrictManager } from "@/api/reseller/types";
import type { ColumnDef } from "@/components/table";
import { DataTable } from "@/components/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyCell } from "@/components/ui/empty-cell";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDebounce } from "@/hooks/use-debounce";
import { AddUserDialog } from "./add-user-dialog";
import { CreateManagerDialog } from "./create-manager-dialog";
import { DistrictManagerSheet } from "./district-manager-sheet";

const PAGE_SIZE = 20;

export function ManagersTable() {
	const { t } = useTranslation();
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(PAGE_SIZE);
	const [search, setSearch] = useState("");
	const [ordering, setOrdering] = useState("");
	const [createOpen, setCreateOpen] = useState(false);
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [addUserFor, setAddUserFor] = useState<CreatedDistrictManager | null>(
		null,
	);
	const debouncedSearch = useDebounce(search);
	const { data, isLoading, isError, refetch } = useCreatedDistrictManagers({
		page,
		page_size: pageSize,
		search: debouncedSearch || undefined,
		ordering: ordering || undefined,
	});

	const columns: ColumnDef<CreatedDistrictManager>[] = [
		{
			key: "company_name",
			label: t("managers.colCompanyName"),
			sortField: "company_name",
			className: "font-medium",
		},
		{
			key: "emails",
			label: t("managers.colEmail"),
			className: "text-muted-foreground",
			render: (row) => {
				const [first, ...rest] = row.emails;
				if (!first) return <EmptyCell />;
				if (rest.length === 0) return first;
				return (
					<div className="flex items-center gap-1.5">
						<span>{first}</span>
						<Tooltip>
							<TooltipTrigger asChild>
								<Badge variant="secondary" className="cursor-default text-xs">
									+{rest.length}
								</Badge>
							</TooltipTrigger>
							<TooltipContent>
								<p className="max-w-xs text-xs">{rest.join(", ")}</p>
							</TooltipContent>
						</Tooltip>
					</div>
				);
			},
		},
		{
			key: "districts_count",
			label: t("managers.colDistrictsCount"),
			className: "text-muted-foreground",
			render: (row) => row.districts_count ?? 0,
		},
		{
			key: "users_count",
			label: t("managers.colUsers"),
			className: "text-muted-foreground",
			render: (row) => row.users_count ?? 0,
		},
		{
			key: "tenants_count",
			label: t("managers.colTenants"),
			className: "text-muted-foreground",
			render: (row) => row.tenants_count ?? 0,
		},
		{
			key: "meters_count",
			label: t("managers.colMetersCount"),
			className: "text-muted-foreground",
			render: (row) => row.meters_count ?? 0,
		},
		{
			key: "_actions",
			label: "",
			render: (row) => (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							aria-label={t("common.actions")}
							onClick={(e) => e.stopPropagation()}
						>
							<DotsThree className="size-4" weight="bold" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem onClick={() => setSelectedId(row.id)}>
							<Info className="size-4" />
							{t("managers.detailsButton")}
						</DropdownMenuItem>
						<DropdownMenuItem onClick={() => setAddUserFor(row)}>
							<UserPlusIcon className="size-4" />
							{t("managers.addUserButton")}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			),
		},
	];

	return (
		<>
			<DataTable
				data={data}
				columns={columns}
				loading={isLoading}
				isError={isError}
				onRetry={() => refetch()}
				page={page}
				pageSize={pageSize}
				onPageChange={setPage}
				onPageSizeChange={(size) => {
					setPageSize(size);
					setPage(1);
				}}
				search={search}
				onSearchChange={(s) => {
					setSearch(s);
					setPage(1);
				}}
				searchPlaceholder={t("managers.searchPlaceholder")}
				ordering={ordering}
				onOrderingChange={(o) => {
					setOrdering(o);
					setPage(1);
				}}
				emptyMessage={
					search ? t("managers.noResults") : t("managers.noManagers")
				}
				headerActions={
					<Button onClick={() => setCreateOpen(true)}>
						<PlusIcon />
						{t("managers.createButton")}
					</Button>
				}
			/>
			<CreateManagerDialog open={createOpen} onOpenChange={setCreateOpen} />
			<DistrictManagerSheet
				managerId={selectedId}
				onClose={() => setSelectedId(null)}
			/>
			{addUserFor ? (
				<AddUserDialog
					open={addUserFor !== null}
					onOpenChange={(open) => !open && setAddUserFor(null)}
					districtManagerId={addUserFor.id}
					companyName={addUserFor.company_name}
				/>
			) : null}
		</>
	);
}
